import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, withScopeId as _withScopeId, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

_pushScopeId("data-v-c4d47086")
const _hoisted_1 = { class: "label" }
const _hoisted_2 = ["type", "name", "value", "disabled"]
const _hoisted_3 = {
  key: 2,
  class: /*#__PURE__*/_normalizeClass(['error'])
}
_popScopeId()

import { useAttrs, computed, ref } from 'vue';
import { HtmlInputEvent } from '@/types';
import useInputStyles from './composables/useInlineInputStyles';
import useInputEvents from '@/components/_global/BalTextInput/composables/useInputEvents';
import useInputValidation from '@/components/_global/BalTextInput/composables/useInputValidation';
import { omit } from 'lodash';

/**
 * TYPES
 */
type InputValue = string | number;
type InputType = 'text' | 'number' | 'date' | 'email' | 'password';
type InputSize = 'xs' | 'sm' | 'md' | 'lg';
type ValidationTrigger = 'input' | 'blur';
type RuleFunction = (val: InputValue) => string;
export type Rules = RuleFunction[];

type Props = {
  name: string;
  modelValue: InputValue;
  isValid?: boolean;
  type?: InputType;
  size?: InputSize;
  disabled?: boolean;
  label?: string;
  inputAlignRight?: boolean;
  decimalLimit?: number;
  validateOn?: ValidationTrigger;
  rules?: Rules;
  format?: (input: string | number) => string | number;
  changeType?: string;
};

/**
 * PROPS & EMITS
 */

// https://v3.vuejs.org/api/sfc-script-setup.html#usage-alongside-normal-script
const __default__ = {
  inheritAttrs: false
};

function setup(__props: {
  name: string;
  modelValue: InputValue;
  isValid?: boolean;
  type?: InputType;
  size?: InputSize;
  disabled?: boolean;
  label?: string;
  inputAlignRight?: boolean;
  decimalLimit?: number;
  validateOn?: ValidationTrigger;
  rules?: Rules;
  format?: (input: string | number) => string | number;
  changeType?: string;
}, { emit }: { emit: ({
  (e: 'blur', value: string): void;
  (e: 'input', value: string): void;
  (e: 'update:modelValue', value: string): void;
  (e: 'update:isValid', value: boolean): void;
  (e: 'keydown', value: HtmlInputEvent);
  (e: 'editToggled', value: boolean): void;
  (e: 'save'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props




/**
 * STATE
 */
const isEditable = ref(false);
const inputElement = ref<HTMLElement>();

/**
 * COMPOSABLES
 */
const attrs = useAttrs();
const { errors, isInvalid, validate } = useInputValidation(props, emit);
const {
  parentClasses,
  inputContainerClasses,
  inputGroupClasses,
  headerClasses,
  footerClasses,
  inputClasses,
  prependClasses,
  appendClasses,
  borderRadiusClasses
} = useInputStyles(props, isInvalid, attrs);
const { onInput, onKeydown, onBlur } = useInputEvents(props, emit, validate);

/**
 * COMPUTED
 */
// We don't want to pass on parent level classes to the html
// input element. So we need to remove it from the attrs object.
const inputAttrs = computed(() => omit(attrs, 'class'));

const inputTextCls = computed(() => {
  if (props.changeType === 'createPool') {
    return {
      rounded: true
    };
  }
  return {
    'bal-text-input': true,
    parentClasses,
    borderRadiusClasses
  };
});

const inputConCls = computed(() => {
  if (props.changeType === 'createPool') {
    return {
      'dark:bg-custom-black-150': true
    };
  }
  return {
    'input-group': true,
    inputGroupClasses
  };
});

/**
 * FUNCTIONS
 */
function toggleEditable() {
  isEditable.value = !isEditable.value;
  emit('editToggled', isEditable.value);
  // if the value was changed to false it means that
  // the user clicked the save icon
  if (isEditable.value === false) emit('save');
  setTimeout(() => {
    if (inputElement.value) {
      inputElement.value.focus();
    }
  }, 200);
}

function handleBlur(e: HtmlInputEvent) {
  toggleEditable();
  onBlur(e);
}

return (_ctx: any,_cache: any) => {
  const _component_BalIcon = _resolveComponent("BalIcon")!
  const _component_BalStack = _resolveComponent("BalStack")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(_unref(inputTextCls))
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(['input-container', _unref(inputContainerClasses), _unref(borderRadiusClasses)])
    }, [
      (_ctx.$slots.header || __props.label)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(['header', _unref(headerClasses)])
          }, [
            _renderSlot(_ctx.$slots, "header", {}, () => [
              _createElementVNode("span", _hoisted_1, _toDisplayString(__props.label), 1)
            ])
          ], 2))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: _normalizeClass(_unref(inputConCls))
      }, [
        _createVNode(_component_BalStack, {
          horizontal: "",
          spacing: "sm",
          class: "w-full"
        }, {
          default: _withCtx(() => [
            (_ctx.$slots.prepend)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(['prepend', _unref(prependClasses)])
                }, [
                  _renderSlot(_ctx.$slots, "prepend")
                ], 2))
              : _createCommentVNode("", true),
            _createElementVNode("input", _mergeProps({
              type: __props.type,
              name: __props.name,
              value: __props.format ? __props.format(__props.modelValue) : __props.modelValue
            }, _unref(inputAttrs), {
              disabled: !isEditable.value || __props.disabled,
              onBlur: handleBlur,
              onInput: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(onInput) && _unref(onInput)(...args))),
              onKeydown: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(onKeydown) && _unref(onKeydown)(...args))),
              class: ['input', _unref(inputClasses)],
              ref: inputElement
            }), null, 16, _hoisted_2),
            _createVNode(_component_BalStack, {
              horizontal: "",
              spacing: "none",
              align: "center"
            }, {
              default: _withCtx(() => [
                (_ctx.$slots.append)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: _normalizeClass(['append', _unref(appendClasses)])
                    }, [
                      _renderSlot(_ctx.$slots, "append")
                    ], 2))
                  : _createCommentVNode("", true),
                (!isEditable.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 1,
                      class: "hover:text-custom-blue-50",
                      onClick: toggleEditable
                    }, [
                      _createVNode(_component_BalIcon, {
                        name: "edit",
                        size: "xs"
                      })
                    ]))
                  : (_openBlock(), _createElementBlock("button", {
                      key: 2,
                      class: "hover:text-custom-blue-50",
                      onClick: toggleEditable
                    }, [
                      _createVNode(_component_BalIcon, {
                        name: "save",
                        size: "xs"
                      })
                    ]))
              ]),
              _: 3
            })
          ]),
          _: 3
        })
      ], 2),
      (_ctx.$slots.footer)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: _normalizeClass(['footer', _unref(footerClasses)])
          }, [
            _renderSlot(_ctx.$slots, "footer")
          ], 2))
        : _createCommentVNode("", true),
      (_unref(isInvalid))
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(errors)[0]), 1))
        : _createCommentVNode("", true)
    ], 2)
  ], 2))
}
}


export default _defineComponent({
  ...__default__,
  props: {
    name: { type: String, required: true },
    modelValue: { type: [String, Number], required: true, default: '' },
    isValid: { type: Boolean, required: false, default: true },
    type: { type: String, required: false, default: 'text' },
    size: { type: String, required: false, default: 'md' },
    disabled: { type: Boolean, required: false, default: false },
    label: { type: String, required: false },
    inputAlignRight: { type: Boolean, required: false, default: false },
    decimalLimit: { type: Number, required: false, default: 18 },
    validateOn: { type: String, required: false, default: 'blur' },
    rules: { type: Array, required: false, default: () => [] },
    format: { type: Function, required: false },
    changeType: { type: String, required: false, default: '' }
  } as unknown as undefined,
  emits: ["blur", "input", "update:modelValue", "update:isValid", "keydown", "editToggled", "save"] as unknown as undefined,
  setup})